import { getFirestore, collection, query, where, getDocs } from 'firebase/firestore';
import { auth } from '../config/firebase';
import { logger } from '../services/loggingService';

export interface PublicRegistration {
  id: string;
  registrationNumber: number;
  firstName: string;
  lastName: string;
  nationality: string;
  representing: string;
  raceDistance: string;
  status: string;
  isOnWaitinglist: boolean;
  waitinglistExpires?: Date | null;
  bib?: string | number | null;
}

const DEBUG_PUBLIC_REGISTRATIONS_FETCH = process.env.REACT_APP_DEBUG_PUBLIC_REGISTRATIONS_FETCH === 'true';

const mapPublicRegistration = (id: string, data: any): PublicRegistration => {
  return {
    id,
    registrationNumber: data.registrationNumber ?? 0,
    firstName: data.firstName ?? '',
    lastName: data.lastName ?? '',
    nationality: data.nationality ?? '',
    representing: data.representing ?? '',
    raceDistance: data.raceDistance ?? '',
    status: data.status ?? '',
    isOnWaitinglist: data.isOnWaitinglist ?? false,
    waitinglistExpires: data.waitinglistExpires?.toDate?.() || null,
    bib: data.bib ?? null,
  };
};

export async function fetchPublicRegistrations(editionId: string): Promise<PublicRegistration[]> {
  const db = getFirestore();
  let publicQueryFailed = false;

  // Prefer the public projection collection. This is safe for unauthenticated visitors.
  try {
    const qPublic = query(
      collection(db, 'publicRegistrations'),
      where('editionId', '==', editionId)
    );
    const snapPublic = await getDocs(qPublic);
    if (!snapPublic.empty) {
      if (DEBUG_PUBLIC_REGISTRATIONS_FETCH) {
        console.info('[fetchPublicRegistrations] source=publicRegistrations', {
          editionId,
          count: snapPublic.size
        });
      }
      const mapped = snapPublic.docs.map((docSnap) => mapPublicRegistration(docSnap.id, docSnap.data()));
      mapped.sort((a, b) => Number(a.registrationNumber) - Number(b.registrationNumber));
      return mapped;
    }

    if (DEBUG_PUBLIC_REGISTRATIONS_FETCH) {
      console.info('[fetchPublicRegistrations] source=publicRegistrations_empty', {
        editionId
      });
    }
  } catch (error) {
    publicQueryFailed = true;
    await logger.warn(
      '[fetchPublicRegistrations] publicRegistrations query failed; falling back',
      {
        editionId,
        collection: 'publicRegistrations',
        query: { where: ['editionId', '==', editionId] },
        authState: auth.currentUser ? 'authenticated' : 'anonymous',
        userId: auth.currentUser?.uid ?? null,
      },
      { error, attentionRequired: true }
    );
    if (DEBUG_PUBLIC_REGISTRATIONS_FETCH) {
      console.info('[fetchPublicRegistrations] source=publicRegistrations_error', {
        editionId
      });
    }
    // ignore and fall back
  }

  // Fallback: for some editions the public projection collection may not be populated.
  // In that case, query registrations directly and map only the public-safe fields.
  try {
    const qPrivate = query(
      collection(db, 'registrations'),
      where('editionId', '==', editionId)
    );
    const snapPrivate = await getDocs(qPrivate);

    if (publicQueryFailed) {
      await logger.warn(
        '[fetchPublicRegistrations] using registrations fallback after public query failure',
        {
          editionId,
          fallbackCollection: 'registrations',
          count: snapPrivate.size,
          authState: auth.currentUser ? 'authenticated' : 'anonymous',
          userId: auth.currentUser?.uid ?? null,
        },
        { attentionRequired: true }
      );
    }

    if (DEBUG_PUBLIC_REGISTRATIONS_FETCH) {
      console.info('[fetchPublicRegistrations] source=registrations_fallback', {
        editionId,
        count: snapPrivate.size
      });
    }

    const mapped = snapPrivate.docs.map((docSnap) => mapPublicRegistration(docSnap.id, docSnap.data()));
    mapped.sort((a, b) => Number(a.registrationNumber) - Number(b.registrationNumber));
    return mapped;
  } catch (error) {
    await logger.error(
      '[fetchPublicRegistrations] registrations fallback query failed',
      {
        editionId,
        collection: 'registrations',
        query: { where: ['editionId', '==', editionId] },
        publicQueryFailed,
        authState: auth.currentUser ? 'authenticated' : 'anonymous',
        userId: auth.currentUser?.uid ?? null,
      },
      { error, attentionRequired: true }
    );
    if (DEBUG_PUBLIC_REGISTRATIONS_FETCH) {
      console.info('[fetchPublicRegistrations] source=registrations_fallback_error', {
        editionId
      });
    }
  }

  return [];
}
